import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from "react";
import { IconButton } from "@bloomberg/media-ui-core";
import { InfoStroke, Lightbulb } from "@bloomberg/media-ui-icons";
import { Popover } from "@bloomberg/media-ui-overlay";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import { ScreenContext } from "@foundation/screen";
import styles from "./styles.module.css";
import ShareFeedback from "~components/ShareFeedback";

interface WatchlistDashboardTitleProps {
  readonly title: string;
  readonly description: React.ReactNode;
  readonly feedbackUrl?: string;
  readonly feedbackTrackingEventLabel?: string;
  readonly localStorageKey?: string;
  readonly tooltipCloseEventLabel: string;
  readonly tooltipOpenEventLabel: string;
}

export default function WatchlistDashboardTitle(
  props: PropsWithChildren<WatchlistDashboardTitleProps>
) {
  const {
    children,
    description,
    feedbackUrl = "https://www.bloomberg.com/markets/watchlist",
    feedbackTrackingEventLabel = "not_provided",
    localStorageKey,
    title,
    tooltipCloseEventLabel,
    tooltipOpenEventLabel,
  } = props;

  const showTooltipOnLoad =
    localStorageKey && localStorage.getItem(localStorageKey) !== "true";

  const [showTooltip, setShowTooltip] = useState(showTooltipOnLoad);
  const [titleElement, setTitleElement] = useState<Element | null>(null);

  const {
    breakpoints: { smallDesktop },
  } = useContext(ScreenContext);

  const toggleTooltip = useCallback(() => {
    setShowTooltip((prevShowTooltip) => {
      const eventLabel = !prevShowTooltip
        ? tooltipOpenEventLabel
        : tooltipCloseEventLabel;

      trackClickEvent("watchlistToolTip", eventLabel, "click", "engagement");

      return !prevShowTooltip;
    });
  }, [tooltipCloseEventLabel, tooltipOpenEventLabel]);

  const closeTooltip = useCallback(() => {
    trackClickEvent(
      "watchlistToolTip",
      tooltipCloseEventLabel,
      "click",
      "engagement"
    );
    setShowTooltip(false);

    // reset local storage key when available
    if (localStorageKey) {
      localStorage.setItem(localStorageKey, "true");
    }
  }, [localStorageKey, tooltipCloseEventLabel]);

  return (
    <div className={styles.titleContainer}>
      <div className={styles.titleWrapper}>
        <span className={styles.title}>{title}</span>
        <IconButton
          className={styles.info}
          size="small"
          onClick={toggleTooltip}
          ref={setTitleElement}
        >
          <InfoStroke />
        </IconButton>
        {showTooltip && titleElement && (
          <Popover
            Icon={<Lightbulb />}
            allowedPlacements={
              smallDesktop
                ? ["right"]
                : [
                    "right",
                    "top",
                    "bottom",
                    "left",
                    "top-start",
                    "top-end",
                    "right-start",
                    "right-end",
                    "bottom-start",
                    "bottom-end",
                    "left-start",
                    "left-end",
                  ]
            }
            eyebrow="How does this work?"
            text={
              <span>
                {description}
                <br />
                <br />
                Have feedback?{" "}
                <ShareFeedback
                  feedbackUrl={feedbackUrl}
                  trackingEventLabel={feedbackTrackingEventLabel}
                />
              </span>
            }
            anchorElement={titleElement}
            onDismiss={closeTooltip}
            acceptZeroDimensionAnchors
          />
        )}
      </div>
      {children}
    </div>
  );
}
